import { useEffect, useState, useMemo, useRef } from "react";
import Slider from "react-slick";
import { store, type RootState } from "../store/store";
import { useDispatch, useSelector } from "react-redux";
import SearchModal from "../components/modals/Searchmodal";
import { setSelectedVehicle } from "../store/orm/userSession";
import ormStationsService from "../hooks/api/stations";
import ORMfunctions from "../hooks/app/general";

import Calcprice from "../components/elements/vehicle/Calcprice";
import Equipments from "../components/elements/vehicle/Equipments";
import { BooleanNullable } from "aws-sdk/clients/glue";

export interface Vehicle {
  id: string;
  text_web: string;
  description_web?: string;
  category_text?: string;
  vehicle_category?: { id: string; name?: string }[];
  features: { value: string; feature: { feature_name: string } }[];
  images: { image: string }[];
  enabled_stations?: string[];
}

interface VehicleListProps {
  special?: string;
  showfilter?: boolean;
  showdetails?: boolean;
  singlecar?: boolean;
  hideFromFilter?: string[];
  singleimg?: string;
  categorypreselect?: string;
  calcprice?: boolean;
}

interface FilterState {
  categories: string[];
  features: Record<string, string[]>;
}

const matchesCriterion = (
  vehicle: Vehicle,
  groupName: string,
  value: string,
) => {
  if (groupName === "categories")
    return vehicle.vehicle_category?.some((cat) => cat.id === value);
  return vehicle.features?.some(
    (f) => f.feature?.feature_name === groupName && f.value === value,
  );
};

function VehicleList({
  special,
  showfilter,
  showdetails,
  singlecar,
  hideFromFilter = [],
  singleimg,
  categorypreselect,
  calcprice = false,
}: VehicleListProps) {
  const dispatch = useDispatch();
  const reduxVehicles = useSelector(
    (state: RootState) => state.vehicles.vehicles as Vehicle[],
  );

  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    features: {},
  });

  const [accordionOpen, setAccordionOpen] = useState<Record<string, boolean>>(
    {},
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [enabledstations, setEnabledstations] = useState<string[] | undefined>(
    undefined,
  );

  function sizeImg(img: string, size = 800) {
    return img.replace(/\/original\//g, `/${size}/`);
  }

  const allFilterOptions = useMemo(() => {
    const categories = new Set<string>();
    const featureGroups: Record<string, Set<string>> = {};
    reduxVehicles.forEach((v) => {
      v.vehicle_category?.forEach((cat) => cat.id && categories.add(cat.id));
      v.features?.forEach((f) => {
        const name = f.feature?.feature_name;
        if (name && f.value && !hideFromFilter.includes(name)) {
          if (!featureGroups[name]) featureGroups[name] = new Set();
          featureGroups[name].add(f.value);
        }
      });
    });
    return {
      categories: Array.from(categories).sort(),
      features: Object.fromEntries(
        Object.entries(featureGroups).map(([k, v]) => [
          k,
          Array.from(v).sort(),
        ]),
      ),
    };
  }, [reduxVehicles, hideFromFilter]);

  // Categorypreselect: Preselect categories based on slug format
  useEffect(() => {
    if (!categorypreselect || !allFilterOptions.categories.length) return;

    let categoriesToSelect: string[] = [];

    // Split by '+' (e.g., "kleinwagen+boot+kombi")
    const slugs = categorypreselect
      .split("+")
      .map((s) => s.trim())
      .filter(Boolean);

    // Match slugs with category IDs
    slugs.forEach((slug) => {
      const matched = allFilterOptions.categories.find(
        (catId) => ORMfunctions.slugMe(catId) === slug,
      );
      if (matched) {
        categoriesToSelect.push(matched);
      }
    });

    // Set filters with valid categories
    if (categoriesToSelect.length > 0) {
      setFilters((prev) => ({
        ...prev,
        categories: categoriesToSelect,
      }));
    }
  }, [categorypreselect, allFilterOptions.categories]);

  const optionCounts = useMemo(() => {
    const counts: Record<string, Record<string, number>> = { categories: {} };
    allFilterOptions.categories.forEach((catId) => {
      counts.categories[catId] = reduxVehicles.filter((v) => {
        if (!matchesCriterion(v, "categories", catId)) return false;
        for (const [fName, fValues] of Object.entries(filters.features)) {
          if (
            fValues.length > 0 &&
            !fValues.some((val) => matchesCriterion(v, fName, val))
          )
            return false;
        }
        return true;
      }).length;
    });
    Object.keys(allFilterOptions.features).forEach((fName) => {
      counts[fName] = {};
      allFilterOptions.features[fName].forEach((val) => {
        counts[fName][val] = reduxVehicles.filter((v) => {
          if (!matchesCriterion(v, fName, val)) return false;
          if (
            filters.categories.length > 0 &&
            !filters.categories.some((c) =>
              matchesCriterion(v, "categories", c),
            )
          )
            return false;
          for (const [oName, oValues] of Object.entries(filters.features)) {
            if (
              oName !== fName &&
              oValues.length > 0 &&
              !oValues.some((ov) => matchesCriterion(v, oName, ov))
            )
              return false;
          }
          return true;
        }).length;
      });
    });
    return counts;
  }, [reduxVehicles, filters, allFilterOptions]);

  const filteredVehicles = useMemo(() => {
    let list = reduxVehicles.filter((v) => {
      if (
        filters.categories.length > 0 &&
        !filters.categories.some((c) => matchesCriterion(v, "categories", c))
      )
        return false;
      for (const [name, values] of Object.entries(filters.features)) {
        if (
          values.length > 0 &&
          !values.some((val) => matchesCriterion(v, name, val))
        )
          return false;
      }
      return true;
    });
    if (special) {
      const idx = list.findIndex((v) => v.id === special);
      if (idx > -1) {
        const [item] = list.splice(idx, 1);
        list.unshift(item);
      }
    }
    return list;
  }, [reduxVehicles, filters, special]);

  const isGroupDisabled = (groupName: string) => {
    const group = optionCounts[groupName];
    return !group || !Object.values(group).some((c) => c > 0);
  };

  useEffect(() => {
    setAccordionOpen((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(prev).forEach((key) => {
        if (prev[key] && isGroupDisabled(key)) {
          next[key] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [optionCounts]);

  const toggleAccordion = (key: string) => {
    if (isGroupDisabled(key)) return;
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  function setCar(cargroup?: Vehicle, useCarCategory?: boolean) {
    if (cargroup) {
      setEnabledstations(cargroup.enabled_stations);
      let categories = filters.categories;
      if (useCarCategory) {
        categories = cargroup!.vehicle_category!.map((item) => item.id);
      }

      dispatch(
        setSelectedVehicle({
          vehicleId: cargroup.id,
          categories: categories,
        }),
      );
    }
    setShowModal(true);
  }

  // function clearCar() {
  //   setEnabledstations([]);
  //   // dispatch(setSelectedVehicle(null));
  //   setShowModal(true);
  // }

  const activeFilterCount =
    filters.categories.length + Object.values(filters.features).flat().length;

  if (singlecar && special) {
    const v = reduxVehicles.find((veh) => veh.id === special);
    if (!v)
      return (
        <div className="orm-vehicle-single-wrapper">
          <p>Fahrzeug nicht gefunden</p>
        </div>
      );
    return (
      <div className="orm-html orm-vehicle-single-wrapper">
        <div className="orm-vehicle-single">
          {(singleimg || v.images?.[0]?.image) && (
            <img
              className="orm-vehicle-single__image"
              src={sizeImg(singleimg || v.images[0].image, 1200)}
              alt={v.text_web}
            />
          )}
          <div className="orm-vehicle-single__content">
            <div className="orm-vehicle-single__left">
              <h3 className="orm-headline__h2">{v.text_web}</h3>
              {showdetails && (
                <p
                  className="orm-headline__h2__subheadline orm-mb-8"
                  dangerouslySetInnerHTML={{
                    __html: v.category_text || v.description_web || "",
                  }}
                />
              )}
              <Equipments features={v.features} />
            </div>
            <div className="orm-vehicle-single__right">
              {calcprice && <Calcprice id={v.id} />}

              <button
                className="orm-button__cta orm-btn__block orm-mt-8"
                onClick={() => setCar(v, true)}
              >
                {ORMfunctions.i18n("general/showOffers") || "Angebote zeigen"}
              </button>
            </div>
          </div>
        </div>
        {showModal && (
          <SearchModal
            enabledstations={enabledstations}
            close={() => {
              setShowModal(false);
              // clearCar();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="orm-html orm-vehicle-list-wrapper">
      {showfilter && (
        <aside className="orm-vehicle-filter">
          <div className="orm-vehicle-filter__header">
            <h2 className="orm-headline__h2">Filter</h2>
            {activeFilterCount > 0 && (
              <button
                onClick={() => setFilters({ categories: [], features: {} })}
                className="orm-vehicle-filter__reset"
              >
                Zurücksetzen ({activeFilterCount})
              </button>
            )}{" "}
            <button
              className="orm-vehicle-filter__button"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              Filter{" "}
              <i
                className={`icon fas ${filterOpen ? "fa-chevron-up" : "fa-chevron-down"}`}
              ></i>
            </button>
          </div>
          <div
            className={`orm-vehicle-filter__options ${filterOpen ? "orm-vehicle-filter__options--active" : ""}`}
          >
            {allFilterOptions.categories.length > 0 && (
              <div
                className={`orm-filter-accordion ${isGroupDisabled("categories") ? "orm-filter-accordion--disabled" : ""}`}
              >
                <button
                  className="orm-filter-accordion__toggle"
                  onClick={() => toggleAccordion("categories")}
                  disabled={isGroupDisabled("categories")}
                >
                  <span>
                    {ORMfunctions.i18n("category/label") || "Fahrzeugtyp"}
                  </span>
                  <i
                    className={`fa orm-pl-8 ${accordionOpen["categories"] ? "fa-caret-up" : "fa-caret-down"}`}
                  ></i>
                </button>
                <div
                  className="orm-filter-accordion__content"
                  hidden={!accordionOpen["categories"]}
                >
                  {allFilterOptions.categories.map((cat) => {
                    const count = optionCounts.categories[cat] || 0;
                    return (
                      <label
                        key={cat}
                        className={`orm-filter-option ${count === 0 ? "orm-filter-option--disabled" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={filters.categories.includes(cat)}
                          disabled={count === 0}
                          onChange={(e) =>
                            setFilters((p) => ({
                              ...p,
                              categories: e.target.checked
                                ? [...p.categories, cat]
                                : p.categories.filter((c) => c !== cat),
                            }))
                          }
                        />
                        <span>
                          {cat} <small>({count})</small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {Object.entries(allFilterOptions.features).map(
              ([fName, values]) => {
                const disabled = isGroupDisabled(fName);
                return (
                  <div
                    key={fName}
                    className={`orm-filter-accordion ${disabled ? "orm-filter-accordion--disabled" : ""}`}
                  >
                    <button
                      className="orm-filter-accordion__toggle"
                      onClick={() => toggleAccordion(fName)}
                      disabled={disabled}
                    >
                      <span>{fName}</span>
                      <i
                        className={`fa orm-pl-8 ${accordionOpen[fName] ? "fa-caret-up" : "fa-caret-down"}`}
                      ></i>
                    </button>
                    <div
                      className="orm-filter-accordion__content"
                      hidden={!accordionOpen[fName]}
                    >
                      {values.map((val) => {
                        const count = optionCounts[fName]?.[val] || 0;
                        return (
                          <label
                            key={val}
                            className={`orm-filter-option ${count === 0 ? "orm-filter-option--disabled" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={
                                filters.features[fName]?.includes(val) || false
                              }
                              disabled={count === 0}
                              onChange={(e) => {
                                const curr = filters.features[fName] || [];
                                setFilters((p) => ({
                                  ...p,
                                  features: {
                                    ...p.features,
                                    [fName]: e.target.checked
                                      ? [...curr, val]
                                      : curr.filter((v) => v !== val),
                                  },
                                }));
                              }}
                            />
                            <span>
                              {val} <small>({count})</small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </aside>
      )}

      <div
        className={`orm-vehicle-list ${showfilter ? "orm-vehicle-list--has-filter" : ""} ${showdetails ? "orm-vehicle-list--details" : ""}`}
      >
        {filteredVehicles.length === 0 ? (
          <p className="orm-vehicle-list__empty">Keine Fahrzeuge gefunden.</p>
        ) : (
          filteredVehicles.map((v) => (
            <article
              key={v.id}
              className={`orm-vehicle-card ${showdetails ? "orm-vehicle-card--details" : ""} ${special === v.id ? "orm-vehicle-card--special" : ""}`}
            >
              <div className="orm-vehicle-card__slider">
                <Slider dots arrows={false} fade infinite speed={500}>
                  {v.images.map((img, i) => (
                    <div className="orm-vehicle-card__slider-coverImg">
                      <img
                        src={sizeImg(img.image, special === v.id ? 1200 : 900)}
                        key={i}
                        alt={v.text_web}
                      />
                    </div>
                  ))}
                </Slider>
              </div>
              <div className="orm-vehicle-card__content">
                <h3 className="orm-headline__h2">{v.text_web}</h3>
                {(special === v.id || showdetails) && (
                  <>
                    <p className="orm-headline__h2__subheadline orm-mb-8 orm-relative">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: v.description_web || "",
                        }}
                      ></span>
                      <i
                        className="fal fa-question-circle orm-cargroup__tooltip"
                        tabIndex={0}
                        role="button"
                        aria-label={ORMfunctions.i18n("cargroup/noguarantee")}
                      >
                        <span
                          className="orm-cargroup__tooltip_text orm-cargroup__tooltip-bottom"
                          role="tooltip"
                        >
                          {ORMfunctions.i18n("cargroup/noguarantee")}
                        </span>
                      </i>
                    </p>

                    <Equipments features={v.features} />
                    {special !== v.id && showdetails && calcprice && (
                      <Calcprice id={v.id} />
                    )}
                  </>
                )}
                {special === v.id && (
                  <span className="orm-vehicle-card__badge">
                    {ORMfunctions.i18n("general/recommended") || "Empfohlen"}
                  </span>
                )}
              </div>
              <div className="orm-vehicle-card__footer">
                {special === v.id && calcprice && <Calcprice id={v.id} />}
                <button
                  className="orm-button__cta orm-btn__block"
                  onClick={() => setCar(v)}
                >
                  {ORMfunctions.i18n("general/showOffers") || "Angebote zeigen"}
                </button>
              </div>
            </article>
          ))
        )}
      </div>
      {showModal && (
        <SearchModal
          enabledstations={enabledstations}
          close={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default VehicleList;
